import { musicSystem } from './music';

/**
 * Spatial audio system using the Web Audio API.
 * Provides 3D positional audio with HRTF panning for realistic
 * directional sound in the FPS environment.
 */
export class SpatialAudioSystem {
  private ctx: AudioContext | null = null;
  private listener: AudioListener | null = null;
  private initialized: boolean = false;
  /** A-M-005: True when ctx was supplied by an external owner (musicSystem)
   *  so dispose() must NOT close it — the owner still needs the context. */
  private ctxIsShared: boolean = false;

  /**
   * Lazily initialize the AudioContext.
   * Must be called after a user gesture (click/keypress) due to browser autoplay policies.
   *
   * A-M-005: Prefer the shared MusicSystem context to avoid a second idle
   * AudioContext on mobile Safari (where every additional context burns
   * scratch buffer memory and competes for the autoplay-unlock gesture).
   * Falls back to allocating a private context only if musicSystem cannot
   * surface one — e.g. in tests that mock `musicSystem` to a partial stub.
   */
  private ensureContext(): AudioContext {
    if (!this.ctx) {
      const shared = (() => {
        try {
          const fn = (musicSystem as { getAudioContext?: () => AudioContext })?.getAudioContext;
          if (typeof fn === 'function') return fn.call(musicSystem);
        } catch { /* musicSystem not materialised — fall through to own ctx */ }
        return undefined;
      })();
      if (shared) {
        this.ctx = shared;
        this.ctxIsShared = true;
      } else {
        this.ctx = new AudioContext();
        this.ctxIsShared = false;
      }
      this.listener = this.ctx.listener;
      this.initialized = true;
    }
    return this.ctx;
  }

  /**
   * Update the listener's position in 3D space.
   * Call this every frame with the local player's camera position.
   * No-op until `resume()` (or a positional source) has initialised the
   * context — calling from the game loop must not create an AudioContext
   * before the user gesture has happened.
   */
  updateListenerPosition(x: number, y: number, z: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const listener = ctx.listener;

    if (listener.positionX !== undefined) {
      // Modern API (AudioParam-based)
      listener.positionX.setValueAtTime(x, ctx.currentTime);
      listener.positionY.setValueAtTime(y, ctx.currentTime);
      listener.positionZ.setValueAtTime(z, ctx.currentTime);
    } else {
      // Legacy fallback
      (listener as any).setPosition(x, y, z);
    }
  }

  /**
   * Update the listener's orientation in 3D space.
   * No-op until the context is initialised (see `updateListenerPosition`).
   * @param forwardX/Y/Z - The direction the listener is facing.
   * @param upX/Y/Z - The up vector of the listener.
   */
  updateListenerOrientation(
    forwardX: number,
    forwardY: number,
    forwardZ: number,
    upX: number,
    upY: number,
    upZ: number,
  ): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const listener = ctx.listener;

    if (listener.forwardX !== undefined) {
      // Modern API
      listener.forwardX.setValueAtTime(forwardX, ctx.currentTime);
      listener.forwardY.setValueAtTime(forwardY, ctx.currentTime);
      listener.forwardZ.setValueAtTime(forwardZ, ctx.currentTime);
      listener.upX.setValueAtTime(upX, ctx.currentTime);
      listener.upY.setValueAtTime(upY, ctx.currentTime);
      listener.upZ.setValueAtTime(upZ, ctx.currentTime);
    } else {
      // Legacy fallback
      (listener as any).setOrientation(forwardX, forwardY, forwardZ, upX, upY, upZ);
    }
  }

  /**
   * Create a PannerNode at a specific 3D position.
   * Uses HRTF for realistic binaural panning, with inverse distance
   * falloff suitable for an FPS arena scale.
   *
   * Wire-Not-Delete (F-AV-014): production positional audio currently
   * routes through Howler's panner inside `audio-manager.playAt` (with
   * the F-AV-002 lowpass filter splice and F-AV-001 listener fix layered
   * on top). This factory is the documented gateway for a future
   * migration off Howler to raw WebAudio HRTF — listener position +
   * orientation are already piped here every frame from
   * `sounds.ts:updateAudioListener`. Keep this hook so a downstream
   * source-routing change does not require reviving deleted plumbing.
   */
  createPositionalSource(x: number, y: number, z: number): PannerNode {
    const ctx = this.ensureContext();

    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 1;
    panner.maxDistance = 500;
    panner.rolloffFactor = 1;
    panner.coneInnerAngle = 360;
    panner.coneOuterAngle = 360;
    panner.coneOuterGain = 1;

    if (panner.positionX !== undefined) {
      panner.positionX.setValueAtTime(x, ctx.currentTime);
      panner.positionY.setValueAtTime(y, ctx.currentTime);
      panner.positionZ.setValueAtTime(z, ctx.currentTime);
    } else {
      (panner as any).setPosition(x, y, z);
    }

    panner.connect(ctx.destination);
    return panner;
  }

  /**
   * Get the underlying AudioContext (e.g., for connecting custom nodes).
   */
  getContext(): AudioContext {
    return this.ensureContext();
  }

  /**
   * Resume the AudioContext after a user gesture.
   * Browsers require this before audio can play.
   */
  async resume(): Promise<void> {
    const ctx = this.ensureContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  }

  /**
   * Check whether the spatial audio system has been initialized.
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Clean up the AudioContext and release resources.
   *
   * A-M-005: If the context is shared (musicSystem-owned), drop our ref
   * but DO NOT close it — the owner still needs it. Closing a shared
   * context would kill music playback on the next play call.
   */
  dispose(): void {
    if (this.ctx) {
      if (!this.ctxIsShared) {
        this.ctx.close().catch(() => {
          // Ignore errors on close -- context may already be closed
        });
      }
      this.ctx = null;
      this.listener = null;
      this.initialized = false;
      this.ctxIsShared = false;
    }
  }
}

export const spatialAudio = new SpatialAudioSystem();
